import type { UIMessage } from 'ai';
import type { ResearchStatus } from '~/hooks/queries/use-research';

/**
 * FRONT-1601 — pure decision logic for "should this open chat refresh its
 * own history because a research run it was watching just settled."
 *
 * The gap this exists to cover: a chat opened as an `InboxChatModal` is
 * bound to its OWN session id. Historically the tab's single `EventSource`
 * (`use-sse.ts`) only listened on the canonical session-store id, so a
 * `chat-response` for any other chat had no live subscriber, queued
 * server-side, and was lost — the briefing was written correctly (a chat
 * message + a saved document), the tab just never asked for it again.
 *
 * That primary path is now fixed: the backend also publishes
 * `chat-response` on the user's own channel (`userChannel(userId)`,
 * src/core/event-bus.ts), which this tab's one EventSource already
 * subscribes to (FRONT-1113) — so a background run finishing on ANY of the
 * user's chats now reaches `use-sse.ts` live, carrying its own `sessionId`.
 * This module is the BACKSTOP for when that live delivery doesn't happen —
 * no SSE connection (page backgrounded, a dropped/reconnecting stream,
 * connection-budget exhaustion) — not the primary mechanism any more.
 * `useResearch` (use-research.ts) already polls this session's run every 3s
 * while it's `running` (FRONT-695, for exactly this no-subscriber gap); this
 * function is the decision of what to do once that poll notices the run
 * leave `running` — deliberately independent of SSE either way.
 *
 * `lastHandledRunId` is the caller's memory of the last run id it already
 * refreshed for (kept in a `useRef` by the caller, same convention as
 * `autoOpenedResearchRunIdRef` in `research-panel-open.ts`) — keyed on the
 * run's own id rather than a plain boolean, because a chat can run research
 * more than once and each run needs its own "already refreshed for this
 * one" memory, independent of whatever the previous run's outcome was.
 *
 * `wasSeenRunning` (FIX 3, adversarial review) is what stops a chat opened
 * AFTER its research already finished from blasting the caches on every
 * open: without it, the very first observation of an already-settled run
 * (mount, `lastHandledRunId` still `null`) reads identically to a genuine
 * running→settled transition, so `shouldRefreshAfterResearch` fired the
 * refresh on every reopen of a chat whose run had long since settled. Only
 * a run the caller has actually watched go through `'running'` counts as
 * "just settled" — see the caller in inbox-chat-modal.tsx for how this is
 * tracked.
 */
export function shouldRefreshAfterResearch(input: {
  /** The session's current research run status — `undefined` when no run
   *  has ever started, or the query hasn't resolved yet. */
  status: ResearchStatus | undefined;
  /** The current run's id, or `null` when there is no run. */
  runId: number | null;
  /** The last run id this caller has already refreshed for, or `null` if
   *  it hasn't refreshed for any run yet. */
  lastHandledRunId: number | null;
  /** Whether the caller has observed THIS run id as `'running'` at some
   *  earlier point (FIX 3). `false` for a run seen already-settled the very
   *  first time — a chat opened after its research had already finished —
   *  which must be treated as already handled rather than a fresh
   *  transition. */
  wasSeenRunning: boolean;
}): boolean {
  const { status, runId, lastHandledRunId, wasSeenRunning } = input;
  if (runId == null) return false;
  // Still in flight, or the query hasn't resolved yet — nothing has settled.
  if (status === undefined || status === 'running') return false;
  if (runId === lastHandledRunId) return false;
  // 'completed' | 'failed' | 'cancelled' all count as settled: a cancelled
  // run delivers nothing new, but firing for it anyway is harmless (uniform
  // with the other two, and the chat row may have changed regardless) —
  // simpler than adding a third branch to special-case it. But only a run
  // this caller actually watched go running → settled counts as "just"
  // settled; one observed already-settled on the very first tick never did.
  return wasSeenRunning;
}

/**
 * FRONT-1601 FIX 4 — the retry schedule for the backstop above.
 *
 * The run row flips to `completed` several journal steps BEFORE the
 * briefing is actually inserted into `chat_messages` (see
 * `src/skills/runner.ts`'s own comment on the gap between the
 * `research-complete` step and the later `message-stamp`/chat-delivery
 * step — plan-settle and the entity-apply step both sit in between). The 3s
 * poll behind `shouldRefreshAfterResearch` can observe `completed` inside
 * that window: the refresh it triggers fires exactly once and stamps the
 * run handled immediately, so if that one refetch lands before the
 * briefing does, it's gone for good — the run is marked handled, nothing
 * ever asks again, and the user is left staring at "Your briefing is in
 * the chat behind this panel" with no briefing there.
 *
 * A few short, bounded retries close that window: keep asking until the
 * briefing shows up in the loaded history, or until `maxAttempts` is spent
 * — whichever comes first. `briefingPresent` always wins over the cap, so a
 * briefing that lands on the very last allowed attempt still counts as
 * found, never as given-up.
 */
export const RESEARCH_REFRESH_RETRY_DELAY_MS = 3_000;
export const RESEARCH_REFRESH_MAX_ATTEMPTS = 5;

export type ResearchRefreshRetryOutcome = 'briefing-found' | 'retry' | 'gave-up';

export function nextResearchRefreshStep(input: {
  /** Whether the run's briefing message is present in the history the last
   *  refetch pulled back — see {@link historyContainsBriefingFor}. */
  briefingPresent: boolean;
  /** How many refresh attempts have been made for this run so far
   *  (including the one whose result is being evaluated right now). */
  attempts: number;
  /** The hard cap — see {@link RESEARCH_REFRESH_MAX_ATTEMPTS}. */
  maxAttempts: number;
}): ResearchRefreshRetryOutcome {
  if (input.briefingPresent) return 'briefing-found';
  if (input.attempts >= input.maxAttempts) return 'gave-up';
  return 'retry';
}

/**
 * FRONT-1601 FIX 4: does `messages` already contain the sourced-briefing
 * message the skill runner writes for research run `runId`? The runner
 * marks that message with a `[run: N]` line (see `research-briefing.ts`'s
 * `extractResearchBriefing`, which owns the full parse/strip — this only
 * needs a presence check, so it mirrors the marker's line shape rather than
 * importing a component module for it). Matches the FULL line, not a
 * substring — `[run: 4]` must never match a message actually marked
 * `[run: 42]`.
 */
export function historyContainsBriefingFor(
  messages: UIMessage[],
  runId: number,
): boolean {
  const markerLine = new RegExp(`^\\[run:\\s*${runId}\\]\\s*$`);
  return messages.some((message) => {
    if (message.role !== 'assistant') return false;
    return message.parts.some(
      (part) =>
        part.type === 'text' &&
        part.text.split('\n').some((line) => markerLine.test(line)),
    );
  });
}
